import axios from "axios";
import {
  calculateNextDueDate,
  PROJECTDATA_URL,
  TOKEN_URL,
} from "../constants/Constants";
import {
  AUTH_TIMER,
  MAINTENANCE_DATA,
  PENDING_CHECKLIST_DATA,
  SHEET_UPDATE_INFO,
  updateGlobalState,
} from "../reducers/GlobalReducer";
import { getAllUniqueOpmProps } from "../axios/objectsAxios";
import { getAllChecklists } from "../axios/checklistsAxios";
import {
  getProjectChecklists,
  getProjectDynamicData,
  getProjectMaintenance,
  getProjectStaticData,
} from "../axios/projectsAxios";
import {
  MODEL_DATA,
  MODEL_DATA_LOADED,
  MODEL_DATA_PROJECT_ID,
  updateModelState,
} from "../reducers/ModelReducer";
import { getAllMaintenance } from "../axios/maintenanceAxios";
import {
  DATA,
  ERROR,
  PENDING,
  updateUserDataState,
} from "../reducers/RequestUserDataReducer";

const normalizeAxiosError = (error) => ({
  data: error?.response?.data ?? null,
  status: error?.response?.status ?? null,
  statusText: error?.response?.statusText ?? "",
  message: error?.message ?? "Request failed",
});

// request some data from the back-end depending on URL passed
export const requestData =
  (backendURL, action, config, callback) => (dispatch) => {
    // dispatch({ type: action.pending });
    dispatch(updateUserDataState({ stateName: PENDING, value: true }));
    axios
      .get(backendURL, config)
      .then((response) => {
        // dispatch({ type: action.success, payload: response.data });
        dispatch(
          updateUserDataState({ stateName: DATA, value: response.data }),
        );
        dispatch(
          updateGlobalState({
            value: response.data.__tokenExpiresIn * 1000,
            stateName: AUTH_TIMER,
          }),
        );
        if (callback) callback();
      })
      .catch((error) => {
        const normalizedError = normalizeAxiosError(error);

        if (normalizedError.status === 401) {
          dispatch(
            updateUserDataState({
              stateName: DATA,
              value: {
                ...(normalizedError.data ?? {}),
                loggedIn: false,
              },
            }),
          );
        }

        // dispatch({ type: action.error, payload: error.response });
        dispatch(
          updateUserDataState({ stateName: ERROR, value: normalizedError }),
        );
        if (callback) callback();
      });
  };

export const getForgeToken = (onGetAccessToken, updateTimer = () => {}) => {
  return axios
    .get(TOKEN_URL, { withCredentials: true })
    .then((data) => {
      onGetAccessToken(data.data.access_token, data.data.expires_in);
      const logoutTime = data.data.expires_in * 1000;
      updateTimer(logoutTime);
      //could use the callback function to update the redux store with a logout timer state that after it hits 0 just logsout and redirects to logout page
    })
    .catch(console.log);
};

var response = [];

export const getHubsData = () => {
  return axios
    .get(PROJECTDATA_URL, { withCredentials: true, params: { type: "hubs" } })
    .then((data) => {
      response = data.data;
    })
    .catch(console.log);
};
let viewerApp;
let viewer;

/* global Autodesk, THREE */ // eslint-disable-line
export async function LoadProjectData(
  dispatch,
  selectedProject,
  allDocs,
  cloudFiles,
  modelDataProjectId,
  modelData,
) {
  const saveProjectWideData = async (urns, projectId) => {
    const options = {
      env: "AutodeskProduction",
      getAccessToken: getForgeToken,
    };

    function loadManifest(documentId) {
      return new Promise((resolve, reject) => {
        try {
          const onDocumentLoadSuccess = (doc) => {
            doc.downloadAecModelData(() => resolve(doc));
          };
          Autodesk.Viewing.Document.load(
            documentId,
            onDocumentLoadSuccess,
            reject,
          );
        } catch (error) {}
      });
    }

    Autodesk.Viewing.Initializer(options, async function () {
      viewer = document.getElementById("viewerDivProject");
      viewerApp = new Autodesk.Viewing.AggregatedView();
      viewerApp.init(viewer, {});
      let docs = [];
      try {
        // **OPTIMIZATION 1: Load data in parallel instead of sequentially**
        try {
          const tasks = [];
          urns.forEach((md) => tasks.push(loadManifest("urn:" + md.urn)));
          // tasks.push(loadManifest("urn:" + urns[0].urn));
          await Promise.all(tasks)
            .then((results) => {
              docs = results;
            })
            .catch((error) => {
              console.error("Error loading manifests:", error);
            });
        } catch (error) {}
        const [
          opmProps,
          allChecklists,
          allProjectChecklists,
          allStaticMaintenance,
          allProjectMaintenance,
        ] = await Promise.all([
          getAllUniqueOpmProps(),
          getAllChecklists(),
          getProjectChecklists(projectId),
          getAllMaintenance(),
          getProjectMaintenance(projectId),
        ]);

        const bubbles = docs
          .map((doc) => {
            const bubbles = doc.getRoot().search({ type: "geometry" });
            return bubbles.length > 0 ? bubbles[0] : null;
          })
          .filter(Boolean);

        viewerApp.setNodes(bubbles);

        await viewerApp.waitForLoadDone({
          propDb: true,
          geometry: true,
        });

        // **OPTIMIZATION 2: Process models in parallel**
        const modelTasks = bubbles.map((bubble, i) => {
          const model = viewerApp.getModel(bubble);
          return onObjectTreeCreated(model, opmProps);
        });

        const modelResults = await Promise.all(modelTasks);

        // Flatten results
        const allData = modelResults.flat();
        // **OPTIMIZATION 3: Process data transformation in parallel**
        await processModelData(
          allData,
          projectId,
          allChecklists,
          allProjectChecklists,
          allStaticMaintenance,
          allProjectMaintenance,
          dispatch,
        );
      } catch (error) {
        console.error("Error in LoadProjectData:", error);
      } finally {
        viewerApp.viewer.finish();
        viewerApp = null;
        Autodesk.Viewing.shutdown();
      }
    });
  };

  const onObjectTreeCreated = async (model, opmProps) => {
    const instanceTree = model.getData().instanceTree;
    const allDbIdsStr = Object.keys(instanceTree.nodeAccess.dbIdToIndex);
    const allDbIdsInt = allDbIdsStr.map(function (id) {
      return parseInt(id);
    });
    return new Promise((resolve, reject) => {
      model.getBulkProperties2(
        allDbIdsInt,
        { propFilter: [...opmProps, "name", "externalId"] },
        //propFilter: ["_RC","MUID", "Tag Number"]
        resolve,
        reject,
      );
    });
  };

  // **OPTIMIZATION 12: Pre-filter and optimize URN creation**
  // Early return optimization
  if (
    selectedProject[0].mviewProjectInfo.id === modelDataProjectId &&
    modelData != null
  ) {
    dispatch(
      updateModelState({
        value: true,
        stateName: MODEL_DATA_LOADED,
      }),
    );
    return;
  }

  dispatch(
    updateModelState({
      value: selectedProject[0].mviewProjectInfo.id,
      stateName: MODEL_DATA_PROJECT_ID,
    }),
  );

  // **OPTIMIZATION 13: Use Map for O(1) lookup instead of Array.find()**
  const nonRetiredDocs = allDocs.filter((doc) => doc.status !== "Retired");
  const docNumberMap = new Map(
    nonRetiredDocs.map((doc) => [doc.docNumber, doc]),
  );
  const tempUrns = cloudFiles
    .map((tempAccModel) => {
      const docNumber = tempAccModel.attributes.displayName.split(".")[0];
      const tempMviewDoc = docNumberMap.get(docNumber);

      return tempMviewDoc
        ? {
            urn: tempAccModel.relationships.derivatives.data.id,
          }
        : null;
    })
    .filter(Boolean);

  await saveProjectWideData(tempUrns, selectedProject[0].mviewProjectInfo.id);
}

async function processModelData(
  allData,
  projectId,
  allChecklists,
  allProjectChecklists,
  allStaticMaintenance,
  allProjectMaintenance,
  dispatch,
) {
  // **OPTIMIZATION 4: Pre-filter and index data for faster lookups**
  const validData = allData.filter((d) =>
    d.properties.some((p) => p.displayName.toLowerCase() === "muid"),
  );
  const muids = validData.map(
    (d) =>
      d.properties.find((p) => p.displayName.toLowerCase() === "muid")
        .displayValue,
  );

  // **OPTIMIZATION 5: Create lookup maps for O(1) access instead of O(n) searches**
  const [staticDataResponse, dynamicDataResponse] = await Promise.all([
    getProjectStaticData(muids),
    getProjectDynamicData(projectId),
  ]);

  // Create lookup maps
  const staticDataMap = new Map(
    staticDataResponse.map((item) => [item.muid, item]),
  );
  const dynamicDataMap = new Map(
    dynamicDataResponse.map((item) => [item.guid, item]),
  );
  const allProjectChecklistsMap = new Map();

  // Pre-process project checklists for faster lookup
  allProjectChecklists?.forEach((checklist) => {
    checklist?.dataLinks?.forEach((dataLink) => {
      if (!allProjectChecklistsMap.has(dataLink.projectObjectId)) {
        allProjectChecklistsMap.set(dataLink.projectObjectId, []);
      }
      allProjectChecklistsMap.get(dataLink.projectObjectId).push(checklist.id);
    });
  });

  // **OPTIMIZATION 6: Process data in chunks to prevent blocking**
  const CHUNK_SIZE = 100;
  const tempModelData = [];
  const tempPendingChecklistsData = [];
  const tempMaintenanceData = [];
  dispatch(
    updateModelState({
      value: true,
      stateName: MODEL_DATA_LOADED,
    }),
  );

  for (let i = 0; i < validData.length; i += CHUNK_SIZE) {
    const chunk = validData.slice(i, i + CHUNK_SIZE);

    // Process chunk
    const chunkResults = await Promise.all(
      chunk.map(async (d) => {
        return processObject(
          d,
          staticDataMap,
          dynamicDataMap,
          allChecklists,
          allProjectChecklistsMap,
          allStaticMaintenance,
          allProjectMaintenance,
        );
      }),
    );

    // Accumulate results
    chunkResults.forEach((result) => {
      tempModelData.push(result.modelData);
      tempPendingChecklistsData.push(...result.pendingChecklists);
      tempMaintenanceData.push(...result.maintenanceData);
    });

    // **OPTIMIZATION 7: Update UI progressively** //Rob - Commented this out as want to avoid confusion
    // if (i % (CHUNK_SIZE * 5) === 0) {
    //   // Update progress every 5 chunks
    // dispatch(
    //   updateModelState({
    //     value: [...tempModelData],
    //     stateName: MODEL_DATA,
    //   })
    // );
    //   // Allow UI to update
    //   await new Promise((resolve) => setTimeout(resolve, 0));
    // }
  }

  // Final dispatch
  dispatch(
    updateModelState({
      value: tempModelData,
      stateName: MODEL_DATA,
    }),
  );

  dispatch(
    updateGlobalState({
      value: tempPendingChecklistsData,
      stateName: PENDING_CHECKLIST_DATA,
    }),
  );
  dispatch(
    updateGlobalState({
      value: tempMaintenanceData,
      stateName: MAINTENANCE_DATA,
    }),
  );
  dispatch(
    updateModelState({
      value: true,
      stateName: MODEL_DATA_LOADED,
    }),
  );
}

function processObject(
  d,
  staticDataMap,
  dynamicDataMap,
  allChecklists,
  allProjectChecklistsMap,
  allStaticMaintenance,
  allProjectMaintenance,
) {
  // **OPTIMIZATION 8: Use Map.get() instead of Array.find() for O(1) lookup**
  const muid = d.properties.find((o) => o.displayName === "MUID")?.displayValue;
  const guid = d.externalId;
  const dbId = d.dbId;
  const name = d.name;
  const tag = d.properties.find(
    (o) => o.displayName === "Tag Number",
  )?.displayValue;

  const staticObjectData = staticDataMap.get(muid);
  const dynamicObjectData = dynamicDataMap.get(guid);

  const category = staticObjectData?.object?.category;
  const objectGroupingId = staticObjectData?.object?.objectGroupingId;

  // **OPTIMIZATION 9: Pre-build property lookup map**
  const propertyMap = new Map(
    d.properties.map((prop) => [prop.displayName, prop.displayValue]),
  );
  const staticPropsMap = new Map(
    staticObjectData?.staticObjectProperties?.map((prop) => [
      prop.property.toLowerCase(),
      prop.value,
    ]) || [],
  );
  const dynamicPropsMap = new Map(
    dynamicObjectData?.projectObjectProperties?.map((prop) => [
      prop.property,
      prop,
    ]) || [],
  );

  const tempObjectProperties =
    staticObjectData?.object?.objectPropertiesMatrices.map((opm) => {
      const tempOpm = { ...opm };
      let tempValue = null;

      switch (tempOpm.objectProperty.origin) {
        case "Static":
          tempValue = staticPropsMap.get(
            opm.objectProperty.property.toLowerCase(),
          );
          tempOpm.objectProperty = {
            ...tempOpm.objectProperty,
            value: tempValue ?? null,
          };
          break;
        case "Dynamic":
          const tempDynaData = dynamicPropsMap.get(opm.objectProperty.property);
          if (tempDynaData) {
            tempOpm.objectProperty = {
              ...tempOpm.objectProperty,
              value: tempDynaData.value ?? null,
            };
            tempOpm.projectObjectPropertyId = tempDynaData.id;
          }
          tempOpm.projectObjectId = dynamicObjectData?.id;
          break;
        case "3D":
        case "P&ID":
          tempValue = propertyMap.get(opm.objectProperty.property);
          tempOpm.objectProperty = {
            ...tempOpm.objectProperty,
            value: tempValue ?? null,
          };
          break;
      }

      return tempOpm;
    }) || [];

  const result = {
    guid,
    name,
    dbId,
    muid,
    tag,
    category,
    properties: tempObjectProperties,
    data: d,
    projectObjectId: dynamicObjectData?.id,
    objectGroupingId,
  };

  // **OPTIMIZATION 10: Process pending checklists more efficiently**
  const muidParts = muid.split("-");
  const muidSubsections = [];
  for (let ii = 0; ii < muidParts.length; ii++) {
    muidSubsections.push(muidParts.slice(0, ii + 1).join("-"));
  }

  const existingChecklistIds =
    allProjectChecklistsMap.get(dynamicObjectData?.id) || [];
  const pendingChecklists = allChecklists.filter(
    (checklist) =>
      checklist.keys?.some((k) => muidSubsections.includes(k)) &&
      !existingChecklistIds.includes(checklist.id),
  );

  const pendingChecklistsData = pendingChecklists.map((checklist) => ({
    ...checklist,
    checklistId: checklist.id,
    projectObjectId: dynamicObjectData?.id,
    tag,
    guid,
    category,
    properties: tempObjectProperties,
    dataLinks: [
      {
        projectObjectId: dynamicObjectData?.id,
        projectObject: {
          ...dynamicObjectData,
          objectPropertiesMatrices: tempObjectProperties,
        },
      },
    ],
    ITP: true,
    status: "Pending",
  }));

  // **OPTIMIZATION 11: Process maintenance records more efficiently**
  const maintenanceData = allStaticMaintenance
    .filter((maintenance) =>
      maintenance.keys?.some((k) => muidSubsections.includes(k)),
    )
    .map((maintenance) => {
      const relevantMaintenanceHistory =
        allProjectMaintenance?.filter(
          (pm) =>
            pm.projectObjectId === dynamicObjectData?.projectObjectId &&
            pm.staticObjectMaintenanceId === maintenance.id,
        ) || [];

      const lastPerformed = relevantMaintenanceHistory.sort(
        (a, b) => new Date(b.createdDate) - new Date(a.createdDate),
      )[0];
      let lastPerformedDate = null;
      let nextDueDate = null;

      if (lastPerformed) {
        lastPerformedDate = new Date(lastPerformed.createdDate);
        nextDueDate = calculateNextDueDate(
          lastPerformedDate,
          maintenance.dimension,
          maintenance.interval,
        );
      }

      return {
        muid: staticObjectData?.muid,
        staticObjectMaintenanceId: maintenance.id,
        interval: maintenance.interval,
        intervalHours: maintenance.intervalHours,
        dimension: maintenance.dimension,
        description: maintenance.description,
        createdDate: maintenance.createdDate,
        createdBy: maintenance.createdBy,
        updatedDate: maintenance.updatedDate,
        updatedBy: maintenance.updatedBy,
        projectObjectId: dynamicObjectData?.projectObjectId,
        projectObject: { ...dynamicObjectData },
        history: relevantMaintenanceHistory,
        lastPerformed: lastPerformedDate,
        nextDue: nextDueDate,
      };
    });

  return {
    modelData: result,
    pendingChecklists: pendingChecklistsData,
    maintenanceData: maintenanceData,
  };
}
